<template>
  <div
    class="card mt-5"
  >
    <div class="card-body">
      <!-- ORDER CREATION FORM -->
      <form @submit.prevent="submit" class="text-start default-form d-flex justify-content-center gap-5" v-if="!order">
        <div class="col-4 form-group">
          <label class="form-label fw-bold">
            {{ $t('advertiser') }}
          </label>
          <div v-if="advertisersLoader">
            <Loader
              :size="'small'"
              :align="'start'"
            />
          </div>
          <div class="custom-select" v-else>
            <select class="form-control" v-model.trim="$v.form.advertiser_id.$model">
              <option disabled="true" selected="true">{{ $t('select') }}</option>
              <template v-if="advertisers.length > 0">
                <option v-for="(lAdvertiser, index) in advertisers" :key="index" :value="lAdvertiser.id">{{ lAdvertiser.name }}</option>
              </template>
            </select>
          </div>
        </div>
        <div class="col-3 form-group">
          <label class="form-label fw-bold">{{ $t('amount') }}</label>
          <Money
            class="form-control mb-3"
            v-model.trim="form.amount"
            v-bind="money"
          />
          <p class="font-size-xSmall mt-2 text-center" v-if="form.amount < 500">{{ $t('minimum_value') }}: {{ 500 | formatMoney(selectedAdvertiser.currency) }}</p>
          <!-- <p class="font-size-xSmall mt-2 text-center" v-if="form.amount > 50000">{{ $t('maximum_value') }}: {{ 50000 | formatMoney(selectedAdvertiser.currency) }}</p> -->
          <p class="text-center font-size-xSmall" v-if="companySplitAmount > 0 && form.amount >= 500 &&  form.amount < 50000">
            {{ $t('company_split_amount.0') }} <strong>{{ companySplitAmount | formatMoney(selectedAdvertiser.currency) }}</strong> {{ $t('company_split_amount.1') }} <span v-b-tooltip.hover :title="$t('company_split_amount_percente', { percente: companySplitPercentage })" class="font-size-small"><i class="fa fa-question-circle"></i></span>
          </p>
        </div>
        <div class="col-4 text-center">
          <Button
            type="submit"
            class="btn btn-color10 mt-8 py-4"
            :loading="loader"
            :disabled="$v.$invalid"
          >
            {{ $t('create_payment_order') }}
          </Button>
        </div>
      </form> <!-- END ORDER CREATION FORM -->
      <!-- ORDER DETAILS -->
      <div v-else>
        <p><strong>{{ $t('payment_order_created_successfully') }}</strong></p>
        <p>
          <strong>{{ $t('advertiser') }}:</strong> {{ order.advertiser.name }}<br/>
          <strong>{{ $t('amount') }}:</strong> {{ (order.amount / 100) | formatMoney(order.advertiser.currency) }}
        </p>
        <p>
          {{ $t('to_make_the_payment') }}, <a :href="order.payment_url" class="link-block-color1" target="_blank">{{ $t('click_here') }}</a>.
        </p>
        <p>
          {{ $t('copy_and_paste_the_address_pagarme') }}
        </p>
        <p>
          <strong>{{ order.payment_url }}</strong>
        </p>
      </div> <!-- END ORDER DETAILS -->
    </div>
  </div>
</template>

<script>

import AdvertisersService from '@/modules/advertisers/services/advertisers-service'
import OrdersService from '@/modules/orders/services/orders-service'
import { getMoneyFormat } from '@/utils'

import Button from '@/components/common/Button/Button'
import Loader from '@/components/common/Loader/Loader'
import { Money } from 'v-money'
import { between, required } from 'vuelidate/lib/validators'

export default {
  name: 'CreateOrderModal',
  props: ['advertiser', 'company', 'reloadAdvertisers'],
  components: {
    Button,
    Loader,
    Money
  },
  data () {
    return {
      advertisers: [],
      advertisersLoader: false,
      selectedAdvertiser: {
        currency: 'BRL'
      },
      order: null,
      form: {
        advertiser_id: '',
        amount: ''
      },
      loader: false,
      money: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        precision: 2,
        masked: false
      }
    }
  },
  validations () {
    return {
      form: {
        advertiser_id: { required },
        amount: {
          required,
          between: between(500, 5000000)
        }
      }
    }
  },
  created () {
    this.setAdvertiser()
  },
  watch: {
    reloadAdvertisers (newValue, oldValue) {
      if (newValue) this.getAdvertisers()
    },
    'form.advertiser_id': function (newVal) {
      this.updateCurrencySettings(newVal)
    }
  },
  computed: {
    /**
     * Amount that will be split to the company
     */
    companySplitAmount () {
      if (!this.company.split || !this.form.amount) return 0
      return this.form.amount * (this.company.split / 100)
    },
    /**
     * Percentage of split
     */
    companySplitPercentage () {
      return this.company.split ? this.company.split : 0
    }
  },
  methods: {
    /**
     * Get advertisers
     */
    async getAdvertisers () {
      this.advertisersLoader = true
      try {
        const advertisers = await AdvertisersService.getAdvertisers({
          companyId: this.company.id,
          itemsPerPage: 100,
          status: 'ACTIVE',
          viewType: 'listing_basic'
        })
        if (this.advertiser) {
          this.advertisers = advertisers.data.filter(item => {
            return item.id === this.advertiser.id
          })
        } else {
          this.advertisers = advertisers.data
        }
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.advertisersLoader = false
    },
    /**
     * On modal hidden
     */
    hidden () {
      this.order = null
      this.form.advertiser_id = ''
      this.form.amount = ''
    },
    /**
     * Set advertiser on load
     */
    async setAdvertiser () {
      if (this.advertisers.length === 0) await this.getAdvertisers()
      if (this.advertiser) this.form.advertiser_id = this.advertiser.id
    },
    /**
     * Submit
     */
    async submit () {
      this.loader = true
      try {
        const order = await OrdersService.createOrder(this.form)
        this.order = order
        this.$emit('order-created', this.order)
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.loader = false
    },
    /**
     * Update currency settings based on advertiser's currency
     */
    updateCurrencySettings (advertiserId) {
      const selectedAdvertiser = this.advertisers.find(advertiser => advertiser.id === advertiserId)

      if (!selectedAdvertiser) return
      this.selectedAdvertiser = selectedAdvertiser
      this.money = getMoneyFormat(selectedAdvertiser.currency)
    }
  }
}
</script>
